import { axiosBaseQuery } from './base.service';
import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpMethod, LeoComparisonPeriod, LeoDashboardAnalysisType } from '@vision/ui/enums';
import { LeoAnalyzeHistory } from '../interfaces/leo-analyze-history';
import {
  HttpBaseRequest,
  LeoAnalysis,
  LeoAnalysisSuggestion,
  LeoAnalysisSuggestionReaction,
  LeoAtaFollowUpRequest,
  LeoFlowAnalysisRequest,
} from '@vision/ui/interfaces';

export interface AnalyzeDashboardArgs {
  accountId: string;
  dashboardId: string;
  dashboardAnalysisType: LeoDashboardAnalysisType;
  purpose: string;
  widgetIds?: string[];
  comparisonPeriod?: LeoComparisonPeriod;
  currentFrom?: string;
  currentTo?: string;
  previousFrom?: string;
  previousTo?: string;
  language?: string;
}

export interface AnalyzeDashboardRequestDto {
  purpose: string;
  widget_ids?: string[];
  comparison_period?: LeoComparisonPeriod;
  current_from?: string;
  current_to?: string;
  previous_from?: string;
  previous_to?: string;
  language?: string;
}

// Wire shape returned by the follow-up endpoint: the cards arrive wrapped in an
// `analysis` envelope. Kept separate from the domain result so a change to that
// envelope stays contained to this service boundary.
interface DashboardAtaFollowUpResponseDto {
  analysis?: { comments?: LeoAnalysisSuggestion[] };
}

// Domain result the app consumes: the flat follow-up card list.
export interface DashboardAtaFollowUpResult {
  comments: LeoAnalysisSuggestion[];
}

function toApi(args: AnalyzeDashboardArgs): AnalyzeDashboardRequestDto {
  return {
    purpose: args.purpose,
    widget_ids: args.widgetIds,
    comparison_period: args.comparisonPeriod,
    current_from: args.currentFrom,
    current_to: args.currentTo,
    previous_from: args.previousFrom,
    previous_to: args.previousTo,
    language: args.language,
  } satisfies AnalyzeDashboardRequestDto;
}

export const LeoService = createApi({
  reducerPath: 'LeoService',
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    analyzeFlow: builder.mutation<{ analysis: LeoAnalysis }, HttpBaseRequest<LeoFlowAnalysisRequest>>({
      query: ({ flowId, purpose, accountId }) => ({
        url: `/v1/leo/flow_analysis/${flowId}?account_id=${accountId}`,
        method: HttpMethod.POST,
        data: { purpose },
        extraOptions: {
          notification: {
            autoHandleSuccess: false,
          },
        },
      }),
    }),
    analyzeDashboard: builder.mutation<{ analysis: LeoAnalysis }, HttpBaseRequest<AnalyzeDashboardArgs>>({
      query: (args) => ({
        url: `/v1/leo/dashboard_${args.dashboardAnalysisType}_analysis/${args.dashboardId}?account_id=${args.accountId}`,
        method: HttpMethod.POST,
        data: toApi(args),
        extraOptions: {
          notification: {
            autoHandleError: false,
            autoHandleSuccess: false,
          },
        },
      }),
    }),
    flowAnalyzeHistory: builder.query<LeoAnalyzeHistory[], void>({
      query: () => ({
        url: `/v1/leo/flows_with_histories`,
        method: HttpMethod.GET,
      }),
    }),
    dashboardAnalyzeHistory: builder.query<LeoAnalyzeHistory[], void>({
      query: () => ({
        url: `/v1/leo/dashboards_with_histories`,
        method: HttpMethod.GET,
      }),
    }),
    quota: builder.query<{ quota: number }, { accountId: string }>({
      query: ({ accountId }) => ({
        url: `/v1/leo/remaining_quota/${accountId}`,
        method: HttpMethod.GET,
      }),
    }),
    favorite: builder.mutation<LeoAnalysis, { analysisId: string }>({
      query: ({ analysisId }) => ({
        url: `/v1/leo/favourite_leo_history/${analysisId}`,
        method: HttpMethod.POST,
        extraOptions: {
          notification: {
            autoHandleSuccess: false,
          },
        },
      }),
    }),
    unfavorite: builder.mutation<LeoAnalysis, { analysisId: string }>({
      query: ({ analysisId }) => ({
        url: `/v1/leo/unfavourite_leo_history/${analysisId}`,
        method: HttpMethod.POST,
        extraOptions: {
          notification: {
            autoHandleSuccess: false,
          },
        },
      }),
    }),
    setSuggestionReaction: builder.mutation<LeoAnalysisSuggestionReaction, { suggestionId: string; reaction: string }>({
      query: ({ suggestionId, reaction }) => ({
        url: `/v1/leo/leo_comment_reaction/${suggestionId}?reaction=${reaction}`,
        method: HttpMethod.POST,
        extraOptions: {
          notification: {
            autoHandleSuccess: false,
          },
        },
      }),
    }),
    destroySuggestionReaction: builder.mutation<void, { reactionId: string }>({
      query: ({ reactionId }) => ({
        url: `/v1/leo/leo_comment_reaction/${reactionId}/destroy`,
        method: HttpMethod.POST,
        extraOptions: {
          notification: {
            autoHandleSuccess: false,
          },
        },
      }),
    }),
    requestQuota: builder.mutation<void, void>({
      query: () => ({
        url: `/v1/leo/send_quota_request`,
        method: HttpMethod.POST,
        extraOptions: {
          notification: {
            translationMessageKey: 'http.message.leo.requestQuota.{{status}}',
          },
        },
      }),
    }),
    dashboardAtaFollowUp: builder.mutation<DashboardAtaFollowUpResult, LeoAtaFollowUpRequest>({
      query: ({ accountId, dashboardId, questionKey, parentCommentId }) => ({
        url: `/v1/leo/dashboard_ata_follow_up/${dashboardId}?account_id=${accountId}`,
        method: HttpMethod.POST,
        data: {
          question_key: questionKey,
          parent_comment_id: parentCommentId,
        },
        extraOptions: { notification: { autoHandleSuccess: false } },
      }),
      transformResponse: (response: DashboardAtaFollowUpResponseDto): DashboardAtaFollowUpResult => ({
        comments: response.analysis?.comments ?? [],
      }),
    }),
  }),
});

export const {
  useAnalyzeDashboardMutation,
  useAnalyzeFlowMutation,
  useDashboardAtaFollowUpMutation,
  useDestroySuggestionReactionMutation,
  useFavoriteMutation,
  useLazyDashboardAnalyzeHistoryQuery,
  useLazyFlowAnalyzeHistoryQuery,
  useLazyQuotaQuery,
  useRequestQuotaMutation,
  useSetSuggestionReactionMutation,
  useUnfavoriteMutation,
} = LeoService;
